import React from 'react';
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  Grid,
  Card,
  CardContent,
  Stack,
  useMediaQuery,
  Tooltip,
  Menu,
  MenuItem,
  Badge,
  Paper,
  Button
} from '@mui/material';
import { CheckCircleOutlined, CloseCircleOutlined, RocketOutlined, HeartOutlined, DownloadOutlined } from '@ant-design/icons';
import IconButton from 'components/@extended/IconButton';
import { useState } from 'react';
import { motion } from 'framer-motion';
import useAntiTamperBlur from 'hooks/useAntiTamperBlur';
import { useSelector, useDispatch } from 'react-redux';
// import G2Logo from 'assets/images/icons/g2.png'; // update with the correct path to G2 logo
// import TrustpilotLogo from 'assets/images/icons/trustpilot-2.svg';
import ReviewsNumbers from './ReviewsNumbers';
import FreeBlockModal from 'sections/premiumBlock/FreeBlockModal';

import { openModal } from 'store/reducers/actions';

const ReviewsOutputGroup = () => {
  const dispatch = useDispatch();
  const matchDownSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const analysisData = useSelector((state) => state.reviewsReducer.analysisData);
  const plan = useSelector((state) => state.proAccountReducer.plan);
  const trialWasActivated = useSelector((state) => state.proAccountReducer?.trial_was_activated);
  const userCountry = useSelector((state) => state.proAccountReducer?.country);
  const restrictedCountries = ['IN', 'VN', 'CN', 'BD', 'PK', 'ID'];
  const trialAllowed = !trialWasActivated && !restrictedCountries.includes(userCountry);
  const isProPlan = plan === 'Trial' || plan === 'Pro' || plan === 'Pro+' || plan === 'Enterprise Pro Max' || plan === 'Enterprise Pro';
  const isModalOpen = useSelector((state) => state.modalReducer.isOpen);

  // Move all useState hooks to the top
  const [isDownloadClicked, setDownloadClicked] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Container for external highlight/scroll behavior (must be declared before any early returns)
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    const clearExternalHighlights = () => {
      if (!containerRef.current) return;
      const nodes = containerRef.current.querySelectorAll('.external-citation-highlight');
      nodes.forEach((node) => {
        const parent = node.parentNode;
        if (!parent) return;
        parent.replaceChild(document.createTextNode(node.textContent), node);
        parent.normalize();
      });
    };

    const applyExternalHighlight = (text) => {
      if (!containerRef.current || !text) return false;
      const base = String(text).trim();
      if (!base) return false;
      const ensureEllipsis = (s) => (/[.…]$/.test(s) || /\.\.\.$/.test(s) ? s : `${s}…`);
      const swapDots = (s) => s.replace(/\.\.\./g, '…');
      const swapEllipsis = (s) => s.replace(/…/g, '...');
      const stripQuotes = (s) => s.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");
      const variants = Array.from(
        new Set([
          base,
          ensureEllipsis(base),
          swapDots(base),
          swapDots(ensureEllipsis(base)),
          swapEllipsis(base),
          stripQuotes(base),
          stripQuotes(ensureEllipsis(base))
        ])
      );
      const walker = document.createTreeWalker(containerRef.current, NodeFilter.SHOW_TEXT, null, false);
      let node;
      while ((node = walker.nextNode())) {
        const content = node.textContent || '';
        const contentLower = content.toLowerCase();
        let foundStart = -1;
        let foundLen = 0;
        for (const v of variants) {
          const vLower = String(v).toLowerCase();
          const i = contentLower.indexOf(vLower);
          if (i !== -1) {
            foundStart = i;
            foundLen = vLower.length;
            break;
          }
        }
        if (foundStart !== -1) {
          try {
            const range = document.createRange();
            range.setStart(node, foundStart);
            range.setEnd(node, foundStart + foundLen);
            const span = document.createElement('span');
            span.className = 'external-citation-highlight';
            span.style.backgroundColor = 'rgba(255, 215, 0, 0.35)';
            span.style.borderRadius = '2px';
            span.style.padding = '1px 2px';
            try {
              range.surroundContents(span);
            } catch (e) {
              span.appendChild(range.extractContents());
              range.insertNode(span);
            }
            setTimeout(() => {
              try {
                span.scrollIntoView({ behavior: 'smooth', block: 'center' });
              } catch (_) {
                /* ignore */
              }
            }, 0);
            return true;
          } catch (e) {
            // continue
          }
        }
      }
      return false;
    };

    const onHighlight = (evt) => {
      const text = evt && evt.detail ? evt.detail.text : '';
      if (!text) return;
      clearExternalHighlights();
      if (!applyExternalHighlight(text)) {
        const stripped = String(text)
          .replace(/["'“”]/g, '')
          .trim();
        if (stripped) applyExternalHighlight(stripped);
      }
    };
    const onClear = () => clearExternalHighlights();
    window.addEventListener('reviews-analysis:highlight', onHighlight);
    window.addEventListener('reviews-analysis:clear-highlight', onClear);
    return () => {
      window.removeEventListener('reviews-analysis:highlight', onHighlight);
      window.removeEventListener('reviews-analysis:clear-highlight', onClear);
      clearExternalHighlights();
    };
  }, []);
  const handleTrialClick = () => {
    if (isProPlan) return;
    dispatch(
      openModal({
        title: 'Get access to full Reviews Analysis',
        subtitle: 'Upgrade to Pro to view all insights from Reviews Analysis.',
        trial: trialAllowed
      })
    );
    setDownloadClicked(false);
  };

  const GateOverlay = ({ children, cta }) => {
    const contentRef = React.useRef(null);
    const overlayRef = React.useRef(null);
    useAntiTamperBlur(contentRef, overlayRef, { blurPx: 5 });
    if (isProPlan) return children;
    return (
      <Box sx={{ position: 'relative' }}>
        <Box ref={contentRef} sx={{ filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.9 }}>
          {children}
        </Box>
        <Paper
          ref={overlayRef}
          elevation={3}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 3,
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(255,255,255,0.6)'
          }}
        >
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.35 }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 6,
                p: { xs: 2, md: 3 },
                width: '92%',
                maxWidth: 720,
                textAlign: 'left'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {cta?.title || 'Unlock full report'}
              </Typography>
              {Array.isArray(cta?.bullets) && cta.bullets.length > 0 && (
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {cta.bullets.map((b, i) => (
                    <Grid key={i} item xs={12} sm={6}>
                      <Typography variant="body2">• {b}</Typography>
                    </Grid>
                  ))}
                </Grid>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleTrialClick}
                  component={motion.button}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  sx={{
                    px: 3,
                    py: 1.25,
                    background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
                    boxShadow: 'none'
                  }}
                >
                  {trialAllowed ? 'Start Free Trial' : 'Upgrade Now'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Cancel anytime
                </Typography>
              </Box>
            </Box>
          </motion.div>
        </Paper>
      </Box>
    );
  };

  // Add null checks for the data but show a loading state instead of returning null
  if (!analysisData || !analysisData.review_analysis_results) {
    return (
      <Grid container spacing={3} sx={{ alignItems: 'center', justifyContent: 'center' }}>
        <Grid item xs={12} md={10}>
          <Typography variant="h6" align="center">
            Loading example report...
          </Typography>
        </Grid>
      </Grid>
    );
  }

  const reviewAnalysisResults = analysisData.review_analysis_results;
  const rating_percentages = analysisData.rating_percentages;
  const review_sources = analysisData.review_sources;

  const renderList = (items, icon, color, maxVisibleFree = 1) => {
    // Add safety check to ensure items is an array
    const itemsArray = Array.isArray(items) ? items : [];

    const visibleItems = isProPlan ? itemsArray : itemsArray.slice(0, maxVisibleFree);

    return (
      <List dense>
        {visibleItems.map((item, index) => (
          <ListItem key={index} sx={{ paddingBottom: '0px' }}>
            <ListItemIcon>{React.createElement(icon, { style: { color } })}</ListItemIcon>
            <ListItemText primary={<Typography variant="body1">{item}</Typography>} />
          </ListItem>
        ))}
      </List>
    );
  };

  const renderProsAndCons = (data, icon, color, maxCategoriesFree = 1, maxItemsPerCategoryFree = 1) => {
    // Add safety check to ensure data is an object
    if (!data || typeof data !== 'object') return null;

    const categoryKeys = Object.keys(data);
    const visibleCategories = isProPlan ? categoryKeys : categoryKeys.slice(0, maxCategoriesFree);

    return visibleCategories.map((key, index) => (
      <Box key={index} sx={{ marginBottom: '16px' }}>
        <Typography variant="h5" color="primary">
          {key}
        </Typography>
        <List dense>
          {Array.isArray(data[key])
            ? (isProPlan ? data[key] : data[key].slice(0, maxItemsPerCategoryFree)).map((item, subIndex) => (
                <ListItem key={subIndex} sx={{ paddingBottom: '0px' }}>
                  <ListItemIcon>{React.createElement(icon, { style: { color } })}</ListItemIcon>
                  <ListItemText primary={<Typography variant="body1">&#34;{item}&#34;</Typography>} />
                </ListItem>
              ))
            : null}
        </List>
      </Box>
    ));
  };

  const handleClick = (event) => {
    if (!isProPlan) {
      dispatch(
        openModal({
          title: 'Need to download results?',
          subtitle: "Upgrade to Pro to download results. Don't miss out on accessing your insights!",
          trial: false
        })
      );
      setDownloadClicked(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const convertToCSV = (objArray) => {
    const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
    let str = '';

    // Add headers
    str += 'Category;Subcategory;Details\n';

    // Add data
    for (const category in array) {
      if (category === 'service' || category === 'tags') {
        str += `${category};;${array[category]}\n`;
      } else if (category === 'feature_requests') {
        array[category].forEach((item) => {
          str += `${category};;${item}\n`;
        });
      } else {
        for (const subcategory in array[category]) {
          array[category][subcategory].forEach((detail) => {
            str += `${category};${subcategory};${detail}\n`;
          });
        }
      }
    }

    return str;
  };

  const downloadCSV = (csvData, filename) => {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadCSV = () => {
    const csvData = convertToCSV(reviewAnalysisResults);
    const serviceName = reviewAnalysisResults['service'] ? reviewAnalysisResults['service'] : 'default';
    const filename = `${serviceName}_reviews_summary.csv`;
    downloadCSV(csvData, filename);
  };

  const escapeRTF = (text) => {
    return text.replace(/[\\{}]/g, '\\$&').replace(/\n/g, '\\line ');
  };

  const formatArrayForRTF = (array) => {
    let rtf = '';
    array.forEach((item) => {
      rtf += `${escapeRTF(item)}\\par\n`;
    });
    return rtf;
  };

  const convertToRTF = (data) => {
    let rtf = '{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0 Arial;}}\n';

    Object.entries(data).forEach(([key, value]) => {
      rtf += `\\b\\fs24 ${escapeRTF(key)}\\b0\\fs20\\par\n`;
      if (Array.isArray(value)) {
        rtf += formatArrayForRTF(value);
      } else if (typeof value === 'object' && value !== null) {
        Object.entries(value).forEach(([subKey, subValue]) => {
          rtf += `\\b ${escapeRTF(subKey)}:\\b0\\par\n`;
          rtf += formatArrayForRTF(subValue);
        });
      } else {
        rtf += `${escapeRTF(value)}\\par\n`;
      }
      rtf += '\\par\n';
    });

    rtf += '}';
    return rtf;
  };

  const downloadDOC = (data) => {
    const rtfContent = convertToRTF(data);
    const blob = new Blob([rtfContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const serviceName = data['service'] ? data['service'] : 'default';
    link.href = url;
    link.download = `${serviceName}_analysis.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadDOC = () => {
    downloadDOC(reviewAnalysisResults);
  };

  return (
    <Grid container spacing={3} sx={{ alignItems: 'center', justifyContent: 'center' }} ref={containerRef}>
      {(!isProPlan && isModalOpen) || isDownloadClicked ? <FreeBlockModal /> : null}
      <Grid item xs={12} md={10}>
        <Stack direction={matchDownSM ? 'column' : 'row'} alignItems="center" justifyContent="center" spacing={2} pb={2}>
          {analysisData['history'] ? (
            <Badge badgeContent="History" color="primary" variant="light" sx={{ mr: 1 }}>
              <Typography variant="h3" ml={3} mr={3} align="center">
                {reviewAnalysisResults['service']}
              </Typography>
            </Badge>
          ) : (
            <Typography variant="h3" ml={3} align="center">
              {reviewAnalysisResults['service']}
            </Typography>
          )}

          <Tooltip title="Download">
            <IconButton onClick={handleClick} size="large" mt={0}>
              <DownloadOutlined />
            </IconButton>
          </Tooltip>

          <Menu
            id="download-menu"
            sx={{
              border: 1, // sets the border width
              // borderColor: 'secondary', // use the secondary color from the theme
              borderRadius: 1 // optional: adds rounded corners
            }}
            // disabled ={ plan === 'Pro' || plan === 'Pro+'}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            aria-labelledby="composition-button"
            PaperProps={{
              sx: {
                border: 1, // sets the border width
                borderColor: 'secondary.light', // use the secondary color from the theme
                borderRadius: 1 // optional: adds rounded corners
              }
            }}
          >
            <MenuItem onClick={handleDownloadCSV}>Download as CSV</MenuItem>
            <MenuItem onClick={handleDownloadDOC}>Download as .doc</MenuItem>
          </Menu>
        </Stack>
      </Grid>
      <Grid item xs={12} md={10}>
        <ReviewsNumbers rating_percentages={rating_percentages} review_sources={review_sources} />
      </Grid>
      <Grid item xs={12} md={10}>
        <Card sx={{ display: 'flex', marginBottom: '32px' }}>
          <Box sx={{ backgroundColor: '#e3f2fd', width: '8px' }} />
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <RocketOutlined style={{ fontSize: '32px', marginRight: '8px' }} />
              <Typography variant="h4" color="primary">
                Most common feature requests
              </Typography>
            </Box>
            <Typography variant="h6" color="textSecondary" sx={{ marginBottom: '16px' }}>
              Discover the most requested features from users. Gain a clear understanding of what enhancements people desire the most.
            </Typography>
            <GateOverlay
              cta={{
                title: 'Unlock Requested Features',
                bullets: ['Full requests list', 'Counts & trends', 'Top priorities']
              }}
            >
              {reviewAnalysisResults.feature_requests.length > 0 ? (
                renderList(reviewAnalysisResults.feature_requests, CheckCircleOutlined, 'blue', 1)
              ) : (
                <Typography>No feature requests available</Typography>
              )}
            </GateOverlay>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={10}>
        <Card sx={{ display: 'flex', marginBottom: '32px' }}>
          <Box sx={{ backgroundColor: '#e8f5e9', width: '8px' }} />
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <HeartOutlined style={{ fontSize: '28px', marginRight: '8px' }} />
              <Typography variant="h4" color="primary">
                Most loved features
              </Typography>
            </Box>
            <Typography variant="body1" color="textSecondary" sx={{ marginBottom: '16px' }}>
              Uncover the most cherished aspects. Learn which features users appreciate the most and the reasons behind their preferences.
            </Typography>
            <GateOverlay
              cta={{
                title: 'Unlock Most Loved Features',
                bullets: ['All pros by theme', 'Full lists & examples']
              }}
            >
              {reviewAnalysisResults.pros ? (
                renderProsAndCons(reviewAnalysisResults.pros, CheckCircleOutlined, 'green', 1, 1)
              ) : (
                <Typography>No Pros available</Typography>
              )}
            </GateOverlay>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={10}>
        <Card sx={{ display: 'flex' }}>
          <Box sx={{ backgroundColor: '#ffebee', width: '8px' }} />
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <CloseCircleOutlined style={{ fontSize: '28px', marginRight: '8px', color: 'red' }} />
              <Typography variant="h4" color="primary">
                Cons based on user feedback
              </Typography>
            </Box>
            <Typography variant="body1" color="textSecondary" sx={{ marginBottom: '16px' }}>
              Identify common drawbacks as highlighted by user feedback. Understand the areas that users feel need improvement.
            </Typography>
            <GateOverlay
              cta={{
                title: 'Unlock Cons from Feedback',
                bullets: ['All cons by theme', 'Full lists & examples']
              }}
            >
              {reviewAnalysisResults.cons ? (
                renderProsAndCons(reviewAnalysisResults.cons, CloseCircleOutlined, 'red', 1, 1)
              ) : (
                <Typography>No Cons available</Typography>
              )}
            </GateOverlay>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ReviewsOutputGroup;
